import { WONDER_IDS } from '@sw/engine';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useMusic } from '../audio/useGameAudio';
import { uiArtUrl } from '../assets/manifest';
import { WonderArt } from '../cards/WonderArt';
import { handleAck } from '../lib/errors';
import { api } from '../net/api';
import { disconnectSocket, emit } from '../net/socket';
import { useRoom } from '../stores/room';
import { useSession } from '../stores/session';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Modal';
import s from '../ui/ui.module.css';
import { NameEntry } from './NameEntry';
import h from './Home.module.css';

export function Backdrop() {
  const art = uiArtUrl('home_backdrop');
  if (art) {
    return (
      <div className={h.backdrop} aria-hidden="true">
        <img src={art} alt="" style={{ gridColumn: '1 / -1', width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <div className={h.backdrop} aria-hidden="true">
      {WONDER_IDS.map((w, i) => (
        <div key={w}>
          <WonderArt wonder={w} side={i % 2 ? 'night' : 'day'} />
        </div>
      ))}
    </div>
  );
}

export function PublicRooms({ compact }: { compact?: boolean }) {
  const { t } = useTranslation();
  const rooms = useRoom((r) => r.publicRooms);
  const status = useSession((x) => x.status);
  const navigate = useNavigate();
  useEffect(() => {
    if (status !== 'online') return;
    void emit('lobby:subscribe', {});
    return () => void emit('lobby:unsubscribe', {});
  }, [status]);
  if (rooms.length === 0) return <p className={h.muted}>{t('home.noPublicRooms')}</p>;
  return (
    <div className={h.roomList}>
      {rooms.slice(0, compact ? 4 : 50).map((r) => (
        <div key={r.code} className={h.roomItem}>
          <div>
            <div className={h.roomCode}>{r.code}</div>
            <div className={h.muted}>
              {t('home.host', { name: r.hostName })} · {t('home.players', { count: r.players })}
            </div>
          </div>
          <Button size="small" onClick={() => navigate(`/sala/${r.code}`)}>
            {t('home.join')}
          </Button>
        </div>
      ))}
    </div>
  );
}

function Menu() {
  const { t } = useTranslation();
  const user = useSession((x) => x.user)!;
  const room = useRoom((r) => r.room);
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    const res = await emit('room:create', { settings: {} });
    setBusy(false);
    if (res.ok) navigate(`/sala/${(res.data as { code: string }).code}`);
    else handleAck(res);
  }

  async function logout() {
    await api.deleteSession().catch(() => undefined);
    disconnectSocket();
    useSession.getState().clear();
  }

  return (
    <>
      <Panel className={h.card}>
        <h2 className={h.cardTitle}>{t('home.greeting', { name: user.username })}</h2>
        {room ? (
          <Button
            variant="primary"
            size="large"
            style={{ width: '100%', marginBottom: 12 }}
            onClick={() => navigate(room.gameId ? `/partida/${room.gameId}` : `/sala/${room.code}`)}
          >
            {t('home.backToRoom', { code: room.code })}
          </Button>
        ) : (
          <>
            <Button variant="primary" size="large" style={{ width: '100%' }} onClick={create} disabled={busy} data-testid="create-room">
              {t('home.create')}
            </Button>
            <p className={h.muted} style={{ textAlign: 'center', margin: '8px 0 0' }}>
              {t('home.createHint')}
            </p>
            <div className={h.divider} />
            <form
              className={h.row}
              onSubmit={(e) => {
                e.preventDefault();
                if (code.trim()) navigate(`/sala/${code.trim().toUpperCase()}`);
              }}
            >
              <input
                className={s.input}
                value={code}
                maxLength={8}
                placeholder={t('home.joinPlaceholder')}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                data-testid="join-code"
              />
              <Button type="submit" disabled={code.trim().length < 4} data-testid="join-submit">
                {t('home.join')}
              </Button>
            </form>
          </>
        )}
        <div className={h.links}>
          <Link to={`/jugador/${encodeURIComponent(user.username)}`}>{t('home.history')}</Link>
          <Link to="/reglas">{t('home.rules')}</Link>
          <a href="#" onClick={(e) => (e.preventDefault(), void logout())}>
            {t('name.change')}
          </a>
        </div>
      </Panel>
      <Panel className={h.card}>
        <h2 className={h.cardTitle}>{t('home.publicRooms')}</h2>
        <PublicRooms compact />
      </Panel>
    </>
  );
}

export function Home() {
  const { t } = useTranslation();
  const token = useSession((x) => x.token);
  const user = useSession((x) => x.user);
  useMusic('lobby');
  return (
    <div className={h.page}>
      <Backdrop />
      <header className={h.hero}>
        <h1 className={h.title}>{t('app.title')}</h1>
        <p className={h.tagline}>{t('app.tagline')}</p>
      </header>
      <main className={h.content}>
        {token && user ? (
          <Menu />
        ) : (
          <Panel className={h.card}>
            <NameEntry />
          </Panel>
        )}
      </main>
    </div>
  );
}
